import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { getApplicationServerKey } from '@/constants/push';
import { toast } from '@/hooks/use-toast';
import { detectPlatformSafe, PlatformInfo, isIOSDevice } from '@/utils/platformDetection';

interface PushSubscriptionData {
  endpoint: string;
  p256dh: string;
  auth: string;
}

export const usePushNotifications = () => {
  const { user } = useAuth();
  const [isSupported, setIsSupported] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [platform, setPlatform] = useState<PlatformInfo | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const resubscriptionAttempted = useRef(false);

  // Check if push notifications are supported - with deferred platform detection for iOS
  useEffect(() => {
    const initializePushSupport = () => {
      try {
        const supported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
        setIsSupported(supported);
        
        if (supported && 'Notification' in window) {
          setPermission(Notification.permission);
        }
        
        // SAFE platform detection - deferred and wrapped
        const detectedPlatform = detectPlatformSafe();
        setPlatform(detectedPlatform);
        setIsInitialized(true);
        
        console.log('📱 Push notification support check:', {
          supported,
          permission: Notification.permission,
          platform: detectedPlatform
        });
      } catch (error) {
        console.warn('Push support check failed:', error);
        setIsInitialized(true);
      }
    };

    // Delay initialization on iOS to prevent Safari crashes
    const isIOS = isIOSDevice();
    const initDelay = isIOS ? 100 : 0;
    
    const timer = setTimeout(initializePushSupport, initDelay);
    return () => clearTimeout(timer);
  }, []);

  // Check current subscription status and handle iOS resubscription
  const checkSubscriptionStatus = useCallback(async () => {
    // Gate behind initialization to prevent early execution on iOS
    if (!isInitialized || !isSupported || !user) return;

    try {
      const registration = await navigator.serviceWorker.getRegistration();
      if (!registration) {
        console.log('❌ No service worker registration found');
        return;
      }

      const subscription = await registration.pushManager.getSubscription();
      setIsSubscribed(!!subscription);

      // Check if we have it stored in the database
      if (subscription) {
        const { data } = await supabase
          .from('push_subscriptions')
          .select('id, created_at')
          .eq('user_id', user.id)
          .eq('endpoint', subscription.endpoint)
          .single();
        
        if (!data) {
          // Subscription exists locally but not in DB, save it
          console.log('📱 Local subscription not in DB, saving...');
          await savePushSubscription(subscription);
        } else {
          // Check subscription age for iOS (iOS subscriptions can expire)
          // Use cached platform state instead of calling detectPlatform again
          if (platform?.isIOS) {
            const createdAt = new Date(data.created_at);
            const ageInDays = (Date.now() - createdAt.getTime()) / (1000 * 60 * 60 * 24);
            
            // iOS subscriptions should be renewed every 30 days to be safe
            if (ageInDays > 30 && !resubscriptionAttempted.current) {
              console.log(`🍎 iOS subscription is ${Math.floor(ageInDays)} days old, renewing...`);
              resubscriptionAttempted.current = true;
              await renewSubscription(registration);
            }
          }
        }
      } else if (permission === 'granted' && platform?.isIOS) {
        // iOS: No local subscription but permission granted - try to resubscribe
        // This handles cases where iOS clears the subscription on app restart
        if (!resubscriptionAttempted.current) {
          console.log('🍎 iOS: Permission granted but no subscription, attempting resubscription...');
          resubscriptionAttempted.current = true;
          await createSubscription(registration);
        }
      }
    } catch (error) {
      console.error('Error checking subscription status:', error);
    }
  }, [isInitialized, isSupported, user, permission, platform]);

  // Renew iOS subscription
  const renewSubscription = async (registration: ServiceWorkerRegistration) => {
    try {
      const existingSubscription = await registration.pushManager.getSubscription();
      if (existingSubscription) {
        // Unsubscribe from old subscription
        await existingSubscription.unsubscribe();
        console.log('🔄 Unsubscribed from old iOS subscription');
      }
      
      // Create new subscription
      await createSubscription(registration);
    } catch (error) {
      console.error('Error renewing iOS subscription:', error);
    }
  };

  // Create new push subscription
  const createSubscription = async (registration: ServiceWorkerRegistration) => {
    try {
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: getApplicationServerKey()
      });
      
      console.log('✅ New push subscription created');
      await savePushSubscription(subscription);
      setIsSubscribed(true);
    } catch (error) {
      console.error('Error creating subscription:', error);
      throw error;
    }
  };

  useEffect(() => {
    checkSubscriptionStatus();
  }, [checkSubscriptionStatus]);

  // Listen for service worker activation messages
  useEffect(() => {
    if (!isSupported) return;

    const handleSWMessage = (event: MessageEvent) => {
      if (event.data?.type === 'SW_ACTIVATED') {
        console.log('📱 Service worker activated, rechecking subscription...');
        resubscriptionAttempted.current = false; // Allow resubscription check
        checkSubscriptionStatus();
      }
    };

    navigator.serviceWorker.addEventListener('message', handleSWMessage);
    return () => {
      navigator.serviceWorker.removeEventListener('message', handleSWMessage);
    };
  }, [isSupported, checkSubscriptionStatus]);

  // Save subscription to Supabase
  const savePushSubscription = async (subscription: PushSubscription) => {
    if (!user) {
      console.error('No user found when trying to save subscription');
      return;
    }

    try {
      const subscriptionData = subscription.toJSON();
      const keys = subscriptionData.keys;
      
      if (!keys?.p256dh || !keys?.auth) {
        throw new Error('Invalid subscription keys');
      }

      // Use cached platform state instead of calling detectPlatform again
      const detectedPlatform = platform || detectPlatformSafe();
      console.log('💾 Saving push subscription for user:', user.id);
      console.log('💾 Platform:', detectedPlatform.isIOS ? `iOS ${detectedPlatform.iosVersion || '?'}` : detectedPlatform.isAndroid ? 'Android' : 'Desktop');
      console.log('💾 Subscription endpoint:', subscription.endpoint.substring(0, 50) + '...');

      // First, check if subscription already exists
      const { data: existing } = await supabase
        .from('push_subscriptions')
        .select('id')
        .eq('user_id', user.id)
        .eq('endpoint', subscription.endpoint)
        .single();

      if (existing) {
        console.log('📱 Subscription already exists, updating...');
        const { error } = await supabase
          .from('push_subscriptions')
          .update({
            p256dh: keys.p256dh,
            auth: keys.auth,
            user_agent: navigator.userAgent,
            updated_at: new Date().toISOString()
          })
          .eq('id', existing.id);
          
        if (error) {
          console.error('Error updating push subscription:', error);
          throw error;
        } else {
          console.log('✅ Push subscription updated successfully');
        }
      } else {
        console.log('📱 Creating new subscription...');
        
        // For iOS, remove older subscriptions for this user to avoid duplicates
        if (detectedPlatform.isIOS) {
          const { data: oldSubs } = await supabase
            .from('push_subscriptions')
            .select('id, endpoint')
            .eq('user_id', user.id);
          
          if (oldSubs && oldSubs.length > 0) {
            // Check for Apple push endpoints
            const appleSubs = oldSubs.filter(s => s.endpoint.includes('push.apple.com'));
            if (appleSubs.length > 0) {
              console.log(`🍎 Removing ${appleSubs.length} old iOS subscription(s)`);
              await supabase
                .from('push_subscriptions')
                .delete()
                .in('id', appleSubs.map(s => s.id));
            }
          }
        }
        
        const { error } = await supabase
          .from('push_subscriptions')
          .insert({
            user_id: user.id,
            endpoint: subscription.endpoint,
            p256dh: keys.p256dh,
            auth: keys.auth,
            user_agent: navigator.userAgent
          });

        if (error) {
          console.error('Error inserting push subscription:', error);
          throw error;
        } else {
          console.log('✅ Push subscription saved successfully');
        }
      }
    } catch (error) {
      console.error('Error saving push subscription:', error);
      throw error;
    }
  };

  // Request notification permission
  const requestPermission = async () => {
    if (!isSupported) {
      toast.error("Push notifications are not supported on this device");
      return false;
    }

    try {
      // Use cached platform state
      const detectedPlatform = platform || detectPlatformSafe();
      
      // iOS 26+ specific guidance
      if (detectedPlatform.isIOS && detectedPlatform.iosVersion && detectedPlatform.iosVersion >= 18) {
        console.log(`🍎 Requesting permission on iOS ${detectedPlatform.iosVersion}`);
      }
      
      const permission = await Notification.requestPermission();
      setPermission(permission);
      
      if (permission === 'granted') {
        toast.success("Notifications enabled successfully!");
        return true;
      } else if (permission === 'denied') {
        const message = detectedPlatform.isIOS 
          ? "Notification permission denied. Go to Settings > Safari > Notifications to enable."
          : "Notification permission denied. You can enable it in your browser settings.";
        toast.error(message);
        return false;
      }
      
      return false;
    } catch (error) {
      console.error('Error requesting permission:', error);
      toast.error("Failed to request notification permission");
      return false;
    }
  };

  // Subscribe to push notifications
  const subscribeUser = async () => {
    if (!isSupported) {
      toast.error('Push notifications are not supported in this browser');
      return false;
    }

    if (!user) {
      toast.error('Please log in to enable notifications');
      return false;
    }

    if (permission !== 'granted') {
      console.log('🔔 Permission not granted, requesting...');
      const granted = await requestPermission();
      if (!granted) return false;
    }

    setLoading(true);
    try {
      console.log('🔄 Subscribing user to push notifications...');
      
      const registration = await navigator.serviceWorker.getRegistration();
      if (!registration) {
        console.error('❌ Service worker not found');
        throw new Error('Service worker not registered');
      }

      console.log('✅ Service worker found, creating subscription...');

      // Check if already subscribed
      let subscription = await registration.pushManager.getSubscription();
      
      if (!subscription) {
        console.log('📱 Creating new push subscription...');
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: getApplicationServerKey()
        });
      } else {
        console.log('📱 Using existing push subscription...');
      }

      console.log('✅ Push subscription created:', subscription.endpoint.substring(0, 50) + '...');

      await savePushSubscription(subscription);
      setIsSubscribed(true);
      toast.success("Push notifications enabled!");
      
      // Reset resubscription flag
      resubscriptionAttempted.current = false;
      
      // Immediately check subscription status to ensure it's properly saved
      setTimeout(() => checkSubscriptionStatus(), 1000);
      
      return true;
    } catch (error) {
      console.error('❌ Error subscribing to push notifications:', error);
      
      // Use cached platform state
      if (platform?.isIOS) {
        toast.error("Failed to enable push notifications. Make sure you've added the app to your home screen.");
      } else {
        toast.error("Failed to enable push notifications");
      }
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Unsubscribe from push notifications
  const unsubscribeUser = async () => {
    if (!isSupported || !user) return false;

    setLoading(true);
    try {
      const registration = await navigator.serviceWorker.getRegistration();
      if (registration) {
        const subscription = await registration.pushManager.getSubscription();
        if (subscription) {
          await subscription.unsubscribe();
        }
      }

      // Remove from database
      await supabase
        .from('push_subscriptions')
        .delete()
        .eq('user_id', user.id);

      setIsSubscribed(false);
      resubscriptionAttempted.current = false;
      toast.success("Push notifications disabled");
      return true;
    } catch (error) {
      console.error('Error unsubscribing:', error);
      toast.error("Failed to disable push notifications");
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Send test notification
  const sendTestNotification = async () => {
    if (!user) return;

    console.log('🧪 Sending test notification for user:', user.id);
    
    try {
      // Get the current session to pass to the Edge Function
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('No active session found');
      }

      console.log('🔑 Session token available:', !!session.access_token);

      // Use cached platform state
      const detectedPlatform = platform || detectPlatformSafe();
      console.log('📱 Sending test to platform:', detectedPlatform.isIOS ? `iOS ${detectedPlatform.iosVersion || '?'}` : detectedPlatform.isAndroid ? 'Android' : 'Desktop');

      const { data, error } = await supabase.functions.invoke('send-web-push', {
        body: {
          toUserId: user.id,
          title: 'Test Notification',
          body: `This is a test push notification from LuvALL! Platform: ${detectedPlatform.isIOS ? 'iOS' : detectedPlatform.isAndroid ? 'Android' : 'Desktop'}`,
          url: '/',
          icon: '/pwa-192x192.png'
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });

      console.log('🧪 Test notification response:', { data, error });

      if (error) {
        console.error('❌ Edge function error:', error);
        if (error.message?.includes('Authentication') || error.message?.includes('401')) {
          throw new Error('Authentication failed. Please try signing out and back in.');
        }
        throw new Error(error.message || 'Failed to send test notification');
      }

      // Check if the response indicates success
      if (data && data.success && data.sent > 0) {
        toast.success(`Test notification sent successfully! (${data.sent} delivered)`);
        return data;
      } else if (data && !data.success) {
        const errorMessage = data.message || 'No active subscriptions found';
        console.warn('⚠️ Test notification not sent:', errorMessage);
        
        // For iOS, provide more specific guidance
        if (platform?.isIOS) {
          toast.error(`${errorMessage}. Try re-enabling notifications.`);
        } else {
          toast.error(errorMessage);
        }
        return data;
      } else {
        throw new Error('Unexpected response format');
      }
    } catch (error: any) {
      console.error('❌ Error sending test notification:', error);
      toast.error(`Error: ${error.message}`);
      throw error;
    }
  };

  // Cleanup on sign out
  useEffect(() => {
    if (!user && isSubscribed) {
      unsubscribeUser();
    }
  }, [user, isSubscribed]);

  return {
    isSupported,
    permission,
    isSubscribed,
    loading,
    platform,
    requestPermission,
    subscribeUser,
    unsubscribeUser,
    sendTestNotification,
    checkSubscriptionStatus
  };
};
